import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile } from '../../types/chemistry';
import { ZONE_COLORS, ZONE_LABELS } from '../../utils/patentZones';

interface ProjectPatentsTabProps {
  projectId: number;
}

interface MoleculeHit {
  id: number;
  smiles: string;
  name?: string;
  zone: number;
  patent_count_exact?: number;
  patent_count_similar?: number;
  max_similarity?: number | null;
  fileId: number;
  fileName: string;
}

interface ZoneSummary {
  zone: number;
  count: number;
  label: string;
  color: string;
}

interface CachedPatentResult {
  molecules: MoleculeHit[];
  zoneSummary: ZoneSummary[];
  totalPatents: number;
  scannedAt: number;
}

// Module-level cache keyed by projectId — survives tab switches
const patentsCache = new Map<number, CachedPatentResult>();

/** Clear cached patent data so next render fetches fresh results from the API.
 *  Called after re-enrichment completes. */
export function invalidatePatentsCache(projectId?: number) {
  if (projectId != null) {
    patentsCache.delete(projectId);
    try { localStorage.removeItem(`patents_cache_${projectId}`); } catch { /* ignore */ }
  } else {
    patentsCache.clear();
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith('patents_cache_'))
        .forEach(k => localStorage.removeItem(k));
    } catch { /* ignore */ }
  }
}

const formatTimeAgo = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes !== 1 ? 's' : ''} ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours} hour${hours !== 1 ? 's' : ''} ago`;
};

// Inline structure image with lazy loading and caching
const svgCache = new Map<string, string>();

const StructureThumbnail: React.FC<{ smiles: string }> = ({ smiles }) => {
  const [svg, setSvg] = useState<string | null>(svgCache.get(smiles) ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (svg || failed || !smiles) return;
    let cancelled = false;
    fetch('/api/v2/indigo/render', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        structure: { value: smiles, type: 'RAW' },
        width: '120px',
        height: '60px',
      }),
    })
      .then(r => r.ok ? r.text() : Promise.reject())
      .then(text => {
        if (!cancelled) {
          svgCache.set(smiles, text);
          setSvg(text);
        }
      })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [smiles, svg, failed]);

  if (failed) {
    return (
      <span className="font-mono text-xs text-gray-400 truncate block max-w-[200px]" title={smiles}>
        {smiles}
      </span>
    );
  }

  if (!svg) {
    return (
      <div className="w-[120px] h-[60px] bg-gray-50 dark:bg-gray-800 rounded flex items-center justify-center">
        <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className="w-[120px] h-[60px] bg-white dark:bg-gray-900 rounded border border-gray-100 dark:border-gray-800 overflow-hidden flex items-center justify-center"
      dangerouslySetInnerHTML={{ __html: svg }}
      title={smiles}
    />
  );
};

const ProjectPatentsTab: React.FC<ProjectPatentsTabProps> = ({ projectId }) => {
  const navigate = useNavigate();
  const cached = patentsCache.get(projectId);
  const [molecules, setMolecules] = useState<MoleculeHit[]>(cached?.molecules ?? []);
  const [zoneSummary, setZoneSummary] = useState<ZoneSummary[]>(cached?.zoneSummary ?? []);
  const [totalPatents, setTotalPatents] = useState(cached?.totalPatents ?? 0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [scannedAt, setScannedAt] = useState<number | null>(cached?.scannedAt ?? null);
  const [, setTick] = useState(0);
  const autoRunRef = useRef(false);

  // Update "time ago" every 30s
  useEffect(() => {
    if (!scannedAt) return;
    const interval = setInterval(() => setTick(t => t + 1), 30000);
    return () => clearInterval(interval);
  }, [scannedAt]);

  // Restore from localStorage on mount
  useEffect(() => {
    if (cached) return; // already have module-level cache
    try {
      const stored = localStorage.getItem(`patents_cache_${projectId}`);
      if (stored) {
        const data: CachedPatentResult = JSON.parse(stored);
        setMolecules(data.molecules);
        setZoneSummary(data.zoneSummary);
        setTotalPatents(data.totalPatents);
        setScannedAt(data.scannedAt);
        patentsCache.set(projectId, data);
      }
    } catch { /* ignore corrupt data */ }
  }, [projectId, cached]);

  const fetchPatentData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setLoadingProgress('Loading files...');

    let cancelled = false;

    try {
      const files = await chemistryApi.getFiles(projectId);
      const dataFiles = files.filter(f => f.moleculesLibraryCount > 0);

      if (dataFiles.length === 0) {
        if (!cancelled) {
          setMolecules([]);
          setZoneSummary([]);
          setTotalPatents(0);
          setIsLoading(false);
        }
        return;
      }

      const allMolecules: MoleculeHit[] = [];
      let patentTotal = 0;

      const batchSize = 5;
      for (let i = 0; i < dataFiles.length; i += batchSize) {
        if (cancelled) return;
        const batch = dataFiles.slice(i, i + batchSize);
        setLoadingProgress(`Scanning files ${i + 1}-${Math.min(i + batchSize, dataFiles.length)} of ${dataFiles.length}...`);

        const results = await Promise.allSettled(
          batch.map(file => chemistryApi.getCollisionMap(file.id))
        );

        for (let j = 0; j < results.length; j++) {
          const result = results[j];
          const file = batch[j];
          if (result.status === 'fulfilled' && result.value) {
            const data = result.value;

            patentTotal += data.summary?.total_patents || 0;

            if (data.molecules) {
              for (const mol of data.molecules) {
                if (mol.zone && mol.zone < 5) {
                  allMolecules.push({
                    id: mol.id,
                    smiles: mol.smiles,
                    name: mol.name,
                    zone: mol.zone,
                    patent_count_exact: mol.patent_count_exact,
                    patent_count_similar: mol.patent_count_similar,
                    max_similarity: mol.max_similarity,
                    fileId: Number(file.id),
                    fileName: file.originalFileName || file.fileName,
                  });
                }
              }
            }
          }
        }
      }

      if (!cancelled) {
        allMolecules.sort((a, b) => {
          if (a.zone !== b.zone) return a.zone - b.zone;
          return (b.max_similarity || 0) - (a.max_similarity || 0);
        });

        // Derive zone counts from molecules (same method as PatentCollisionMap)
        const zoneCountFromMols: Record<number, number> = {};
        for (const mol of allMolecules) {
          zoneCountFromMols[mol.zone] = (zoneCountFromMols[mol.zone] || 0) + 1;
        }
        const summary: ZoneSummary[] = [];
        for (let z = 1; z <= 5; z++) {
          if (zoneCountFromMols[z]) {
            summary.push({
              zone: z,
              count: zoneCountFromMols[z],
              label: ZONE_LABELS[z] || `Zone ${z}`,
              color: ZONE_COLORS[z] || '#808080',
            });
          }
        }

        const now = Date.now();
        setMolecules(allMolecules);
        setZoneSummary(summary);
        setTotalPatents(patentTotal);
        setScannedAt(now);
        setIsLoading(false);

        // Cache results (module-level + localStorage)
        const cacheData: CachedPatentResult = {
          molecules: allMolecules,
          zoneSummary: summary,
          totalPatents: patentTotal,
          scannedAt: now,
        };
        patentsCache.set(projectId, cacheData);
        try { localStorage.setItem(`patents_cache_${projectId}`, JSON.stringify(cacheData)); } catch { /* ignore */ }
      }
    } catch (err: any) {
      if (!cancelled) {
        setError(err?.response?.data?.detail || err?.message || 'Failed to load patent data');
        setIsLoading(false);
      }
    }

    return () => { cancelled = true; };
  }, [projectId]);

  // Auto-run patent scan on mount if no cached data
  useEffect(() => {
    if (!autoRunRef.current && !patentsCache.has(projectId)) {
      // Check localStorage first
      try {
        const stored = localStorage.getItem(`patents_cache_${projectId}`);
        if (stored) return; // localStorage restore effect already handled it
      } catch { /* ignore */ }
      autoRunRef.current = true;
      fetchPatentData();
    }
  }, [projectId, fetchPatentData]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <span className="text-gray-500 dark:text-gray-400 font-medium">{loadingProgress}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-red-500 bg-red-50 px-6 py-4 rounded-xl font-medium">{error}</div>
      </div>
    );
  }

  if (molecules.length === 0 && zoneSummary.length === 0) {
    if (scannedAt) {
      // Scan completed but found no patent-relevant molecules
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <span className="text-2xl mb-2">&#x2705;</span>
          <span className="text-gray-600 dark:text-gray-300 font-medium">No patent conflicts found</span>
          <span className="text-gray-400 dark:text-gray-500 text-sm mt-1">All molecules appear to have freedom to operate</span>
          <button
            onClick={fetchPatentData}
            className="mt-4 px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 border border-emerald-200 dark:border-emerald-800 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
          >
            Rescan
          </button>
        </div>
      );
    }
    // Auto-scan should be starting via useEffect
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-12 h-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mb-4" />
        <span className="text-gray-500 dark:text-gray-400 font-medium">Scanning patents...</span>
      </div>
    );
  }

  const totalHits = zoneSummary.reduce((sum, z) => sum + (z.zone < 5 ? z.count : 0), 0);
  const totalMolecules = zoneSummary.reduce((sum, z) => sum + z.count, 0);

  return (
    <div className="p-6 max-w-7xl mx-auto w-full space-y-6">
      {/* Header with rescan */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {scannedAt && (
            <>Patent analysis was last run {formatTimeAgo(scannedAt)}.</>
          )}
        </span>
        <button
          onClick={fetchPatentData}
          className="px-3 py-1.5 text-xs font-medium text-emerald-600 hover:text-emerald-700 border border-emerald-200 dark:border-emerald-800 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
        >
          Rescan
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">{totalMolecules.toLocaleString()}</span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase font-semibold tracking-wide">Analysed</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
          <span className="text-2xl font-bold text-red-600">{totalHits.toLocaleString()}</span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase font-semibold tracking-wide">Patent Hits</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
          <span className="text-2xl font-bold text-gray-800 dark:text-gray-200">{totalPatents.toLocaleString()}</span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase font-semibold tracking-wide">Patents</p>
        </div>
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
          <span className="text-2xl font-bold text-emerald-600">
            {totalMolecules > 0 ? ((zoneSummary.find(z => z.zone === 5)?.count || 0) / totalMolecules * 100).toFixed(0) : 0}%
          </span>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 uppercase font-semibold tracking-wide">Novel</p>
        </div>
      </div>

      {/* Zone breakdown */}
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 p-4">
        <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Zone Breakdown</h3>
        <div className="space-y-2">
          {zoneSummary.map((zone) => {
            const pct = totalMolecules > 0 ? (zone.count / totalMolecules) * 100 : 0;
            return (
              <div key={zone.zone} className="flex items-center gap-3">
                <div
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: zone.color }}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400 w-32 flex-shrink-0">{zone.label}</span>
                <div className="flex-1 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${pct}%`, backgroundColor: zone.color }}
                  />
                </div>
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 w-16 text-right">
                  {zone.count.toLocaleString()}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Molecule table */}
      {molecules.length > 0 && (
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-100 dark:border-gray-800">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest">
              Molecules with Patent Hits ({molecules.length.toLocaleString()})
            </h3>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 'calc(100vh - 500px)' }}>
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Zone</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Structure</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Similarity</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Exact</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Similar</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Source File</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                {molecules.slice(0, 200).map((mol) => (
                  <tr
                    key={`${mol.fileId}-${mol.id}`}
                    className="hover:bg-emerald-50/50 dark:hover:bg-gray-800 cursor-pointer transition-colors"
                    onClick={() => navigate(`/chemistry/file/${mol.fileId}/editor`)}
                  >
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: ZONE_COLORS[mol.zone] || '#808080' }}
                        />
                        <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {ZONE_LABELS[mol.zone] || `Zone ${mol.zone}`}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <StructureThumbnail smiles={mol.smiles} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                        {mol.max_similarity != null ? `${(mol.max_similarity * 100).toFixed(0)}%` : '\u2014'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {mol.patent_count_exact || 0}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                        {mol.patent_count_similar || 0}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400 truncate block max-w-[200px]" title={mol.fileName}>
                        {mol.fileName.replace(/^[a-f0-9]{8}_/i, '')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {molecules.length > 200 && (
              <div className="px-4 py-3 text-center text-xs text-gray-400 border-t border-gray-100 dark:border-gray-800">
                Showing first 200 of {molecules.length.toLocaleString()} molecules. Open individual files to see all.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPatentsTab;
